import React, {useMemo, useState} from 'react';
import {useMediaQuery} from 'react-responsive';
import TimiDataGrid from '../../../../components/shared/grid';
import moment from 'moment';
import {
    GridRef,
    GridColumnProps,
    GridGroupByProps,
    ExpandedGroupsRef
} from '../../../../components/shared/grid/types';
import {Lx} from  '../../../../i18n/consts';
import {IntlShape} from 'react-intl';
import GroupEmployeeRow from '../group-employee-row';
import styles from './time-report-grid.module.scss';
import classNames from 'classnames';
import {
    GetGroupedAbsenceHours,
    GetGroupedApprovedHours,
    GetGroupedBilledHours,
    GetGroupedOvertimes,
    GetGroupedTimeBankBalance,
    GetGroupedWorkHours,
    TimeReportItem
} from '../../../../features/time-report/types';
import {CollapseButton} from '../../../../components/shared/collapse-button';
import { TimeSheetGroupType } from '../../../../features/schedule/types';
import { getTimeReportItemTypeLabel } from '../../helpers/filter-time-report-items';
import {TimeReportV2DailyEmployeeTotal} from '../../../../features/time-report-v2/types';
import {Tooltip} from '@mui/material';
import {getTimeReportEmployeePlannedHours} from '../../helpers/time-report-group-hours';

interface TimeReportGridProps {
    data: TimeReportItem[];
    intl: IntlShape;
    groupBy: TimeSheetGroupType;
    detailedByDay: boolean;
    employeeTotals?: TimeReportV2DailyEmployeeTotal[];
    /* Group keys to re-open after returning from a sub-page. Stable per mount. */
    initialExpandedGroupKeys?: Array<string>;
}

type TimeReportViewConfig = {
    detailedByDay: boolean;
    isProjectView: boolean;
    isProjectSummaryView: boolean;
    isProjectDetailedView: boolean;
    isEmployeeDetailedView: boolean;
    showDetailedComment: boolean;
    hideProjectDetailedCommentColumn: boolean;
    showClientColumn: boolean;
    useWideSummaryColumns: boolean;
};

const getGroupBy = (
    groupType: TimeSheetGroupType,
    detailedByDay: boolean,
    employeeTotals: TimeReportV2DailyEmployeeTotal[] = []
): GridGroupByProps => {
    const topLevelField = groupType === TimeSheetGroupType.EMPLOYEEVIEW ? 'employeeName' : 'projectName';
    const showClientInGroup = groupType === TimeSheetGroupType.PROJECTVIEW && !detailedByDay;
    const employeeTotalsById = new Map(employeeTotals.map((total) => [total.employeeId, total.plannedHours]));
    const showTimeBankBalance = (groupField: string): boolean => (
        groupField === 'employeeName'
        || (groupType === TimeSheetGroupType.PROJECTVIEW && groupField === 'projectName')
    );
    const showPlannedHoursColumn = groupType === TimeSheetGroupType.EMPLOYEEVIEW || !detailedByDay;

    return {
        rowGroupingModel: groupType === TimeSheetGroupType.EMPLOYEEVIEW
            ? ['employeeName']
            : ['projectName', 'employeeName'],
        groupByRenderer: (groupField, groupFieldValue, value) => {
            const items = value as TimeReportItem[];
            const showGroupPlannedHours = showPlannedHoursColumn;
            const showPlannedHoursValue = showGroupPlannedHours && groupField === 'employeeName';
            return(<GroupEmployeeRow
                title={groupFieldValue}
                clientName={showClientInGroup && groupField === topLevelField ? items[0]?.clientName ?? '' : undefined}
                workHours={GetGroupedWorkHours(items)}
                showPlannedHours={showGroupPlannedHours}
                plannedHours={showPlannedHoursValue
                    ? getTimeReportEmployeePlannedHours(
                        items,
                        employeeTotalsById,
                        detailedByDay && groupType === TimeSheetGroupType.EMPLOYEEVIEW
                    )
                    : undefined}
                approvedHours={GetGroupedApprovedHours(items)}
                billedHours={GetGroupedBilledHours(items)}
                absenceHours={GetGroupedAbsenceHours(items)}
                overtimeHours={GetGroupedOvertimes(items)}
                timeBankBalance={showTimeBankBalance(groupField) ? GetGroupedTimeBankBalance(items) : undefined}
                compact={groupField !== topLevelField} />)
        }
    }
}

const widthHourColumn = 64;
const widthSummaryHourColumn = 84;
const widthSummaryHourColumnWide = 100;
const getViewConfig = (
    groupType: TimeSheetGroupType,
    detailedByDay: boolean,
    hideDetailedComment: boolean,
    useWideSummaryColumns: boolean
): TimeReportViewConfig => {
    const isProjectView = groupType === TimeSheetGroupType.PROJECTVIEW;
    const isProjectSummaryView = isProjectView && !detailedByDay;
    const isProjectDetailedView = isProjectView && detailedByDay;
    const isEmployeeDetailedView = groupType === TimeSheetGroupType.EMPLOYEEVIEW && detailedByDay;

    return {
        detailedByDay,
        isProjectView,
        isProjectSummaryView,
        isProjectDetailedView,
        isEmployeeDetailedView,
        showDetailedComment: detailedByDay && !hideDetailedComment,
        hideProjectDetailedCommentColumn: isProjectDetailedView && hideDetailedComment,
        showClientColumn: !isEmployeeDetailedView && !isProjectSummaryView,
        useWideSummaryColumns,
    };
};

const getDateColumn = (): GridColumnProps => ({
    field: 'date',
    localizationKey: Lx.General.DATE,
    noHeader: true,
    editable: false,
    width: 130,
    value: (row) => (row as TimeReportItem).date,
    displayValue: (_value, row) => {
        const date = moment((row as TimeReportItem).date, 'YYYY-MM-DD', true);

        if (!date.isValid()) {
            return <div className={styles.textCell}>{(row as TimeReportItem).date}</div>;
        }

        return (
            <div className={styles.dateCell}>
                <span>{date.format('L')}</span>
                <span className={styles.dateWeekday}>{date.format('dd')}</span>
            </div>
        );
    },
    alignment: 'start'
});

const getProjectGroupColumn = (): GridColumnProps => ({
    field: 'projectGroup',
    editable: false,
    flex: { grow: 2 },
    value: (row) => (row as TimeReportItem).id,
    displayValue: (_value, row) => (
        <div className={styles.textCell}>{(row as TimeReportItem).employeeName}</div>
    ),
    alignment: 'start',
});

const getClientGroupColumn = (): GridColumnProps => ({
    field: 'clientGroup',
    localizationKey: Lx.General.CLIENT,
    editable: false,
    flex: { grow: 1 },
    value: (row) => (row as TimeReportItem).id,
    displayValue: () => <div className={styles.textCell} />,
    alignment: 'start',
});

const getClientColumn = (view: TimeReportViewConfig): GridColumnProps => ({
    field: 'client',
    localizationKey: Lx.General.CLIENT,
    editable: false,
    width: view.isEmployeeDetailedView ? 110 : undefined,
    flex: view.isEmployeeDetailedView ? undefined : { grow: 1 },
    value: (row) => view.isProjectDetailedView ? (row as TimeReportItem).id : (row as TimeReportItem).clientId,
    displayValue: (_value, row) => (
        <div className={styles.textCell}>
            {view.isProjectDetailedView ? (row as TimeReportItem).employeeName : (row as TimeReportItem).clientName}
        </div>
    ),
    alignment: 'start'
});

const getProjectColumn = (view: TimeReportViewConfig): GridColumnProps => ({
    field: 'project',
    localizationKey: view.isProjectDetailedView ? undefined : view.isProjectView ? Lx.General.EMPLOYEE : Lx.General.PROJECT,
    editable: false,
    flex: { grow: view.isProjectDetailedView ? 1 : 2 },
    value: (row) => view.isProjectDetailedView
        ? (row as TimeReportItem).comment
        : view.isProjectView
            ? (row as TimeReportItem).id
            : (row as TimeReportItem).projectId,
    displayValue: (_value, row) => (
        <div className={styles.textCell}>
            {view.isProjectDetailedView
                ? (row as TimeReportItem).comment
                : view.isProjectView
                    ? (row as TimeReportItem).employeeName
                    : (row as TimeReportItem).projectName}
        </div>
    ),
    alignment: 'start',
});

const getTypeColumn = (): GridColumnProps => ({
    field: 'type',
    localizationKey: Lx.General.TYPE,
    editable: false,
    width: 130,
    value: (row) => getTimeReportItemTypeLabel(row as TimeReportItem),
    displayValue: (_value, row) => (
        <div className={styles.textCell}>{getTimeReportItemTypeLabel(row as TimeReportItem)}</div>
    ),
    alignment: 'start',
});

const getCommentColumn = (): GridColumnProps => ({
    field: 'comment',
    editable: false,
    flex: { grow: 2 },
    value: (row) => (row as TimeReportItem).comment,
    displayValue: (_value, row) => (
        <div className={styles.textCell}>{(row as TimeReportItem).comment}</div>
    ),
    alignment: 'start',
});

const getHourColumns = (view: TimeReportViewConfig): GridColumnProps[] => {
    const columns: GridColumnProps[] = [];

    if (!view.isProjectDetailedView) {
        columns.push({
            field: 'plannedHours',
            localizationKey: Lx.Schedule.PLANNED_HOURS_ABBREVIATION,
            headerTooltipKey: Lx.Schedule.PLANNED_HOURS,
            type: 'number',
            width: widthHourColumn,
            displayValue: (_value, row) => {
                const item = row as TimeReportItem;
                const value = view.isProjectView && !view.detailedByDay ? item.plannedHours : undefined;
                return <div className={classNames({[styles.cell]:true,[styles.numericCell]:true,[styles.cellRegular]:true})}>{!value ? '' : value}</div>;
            },
            alignment: 'end'
        });
    }

    columns.push(
        {
            field: 'timeBankBalance',
            localizationKey: Lx.Schedule.TIME_BANK_BALANCE_ABBREVIATION,
            headerTooltipKey: Lx.Schedule.TIME_BANK_BALANCE,
            type: 'number',
            width: widthHourColumn,
            displayValue: (_value, row) => {
                const item = row as TimeReportItem;
                const value = item.timeBankHours;
                return <div className={classNames({
                    [styles.cell]: true,
                    [styles.numericCell]: true,
                    [styles.cellRegular]: true
                })}>{value === undefined || (view.detailedByDay && value == 0) ? '' : value}</div>;
            },
            alignment: 'end'
        },
        {
            field: 'absenceHours',
            localizationKey: Lx.Schedule.ABSENCE_HOURS_ABBREVIATION,
            headerTooltipKey: Lx.Schedule.ABSENCE_HOURS,
            width: widthHourColumn,
            type: 'number',
            displayValue: (_value, row) => {
                const item = row as TimeReportItem;
                return <div className={classNames({[styles.cell]:true,[styles.numericCell]:true,[styles.absence]: true})}>{item.absenceHours == 0 ? '' : item.absenceHours}</div>
            },
            alignment: 'end'
        },
        {
            field: 'overtime',
            localizationKey: Lx.Schedule.OVERTIME_HOURS_ABBREVIATION,
            headerTooltipKey: Lx.Schedule.OVERTIME_HOURS,
            type: 'number',
            width: widthHourColumn,
            displayValue: (_value, row) => {
                const item = row as TimeReportItem;
                return <div className={classNames({[styles.cell]:true,[styles.numericCell]:true,[styles.cellRegular]:true})}>{item.overtimes == 0 ? '' : item.overtimes}</div>
            },
            alignment: 'end'
        },
        {
            field: 'workedHours',
            localizationKey: Lx.Schedule.WORKED_HOURS_ABBREVIATION,
            headerTooltipKey: Lx.Schedule.WORKED_HOURS,
            type: 'number',
            width: widthHourColumn,
            displayValue: (_value, row) => {
                const item = row as TimeReportItem;
                return <div className={classNames({[styles.cell]:true,[styles.numericCell]:true,[styles.cellRegular]:true})}>{item.workHours == 0 ? '' : item.workHours}</div>;
            },
            alignment: 'end'
        },
        {
            field: 'approved',
            localizationKey: Lx.Schedule.APPROVED_HOURS,
            type: 'number',
            width: view.useWideSummaryColumns ? widthSummaryHourColumnWide : widthSummaryHourColumn,
            displayValue: (_value, row) => {
                const item = row as TimeReportItem;
                return item.approvedHours > 0
                    ? <div className={classNames({[styles.cell]:true,[styles.numericCell]:true,[styles.approved]:true})}>{item.approvedHours}</div>
                    : <div className={classNames({[styles.cell]:true,[styles.numericCell]:true,[styles.approved]:true,[styles.isZero]:true})}>{'0'}</div>
            },
            alignment: 'end'
        },
        {
            field: 'billable',
            localizationKey: Lx.Schedule.BILLABLE_HOURS,
            type: 'number',
            width: widthSummaryHourColumn,
            displayValue: (_value, row) => {
                const item = row as TimeReportItem;
                return item.billedHours > 0
                    ? <div className={classNames({[styles.cell]:true,[styles.numericCell]:true,[styles.billed]:true})}>{item.billedHours}</div>
                    : <div className={classNames({[styles.cell]:true,[styles.numericCell]:true,[styles.billed]:true,[styles.isZero]:true})}>{'0'}</div>
            },
            alignment: 'end'
        }
    );

    return columns;
};

const assignCollapseButton = (columns: GridColumnProps[]): GridColumnProps[] => {
    const firstHeaderColumnIndex = columns.findIndex((column) => !column.noHeader);

    if (firstHeaderColumnIndex < 0) {
        return columns;
    }

    return columns.map((column, index) => (
        index === firstHeaderColumnIndex
            ? { ...column, isHeaderCollapseButton: true }
            : column.isHeaderCollapseButton
                ? { ...column, isHeaderCollapseButton: false }
                : column
    ));
};

const buildTimeReportColumns = (view: TimeReportViewConfig): GridColumnProps[] => {
    const leadingColumns: GridColumnProps[] = [];
    const detailedColumns: GridColumnProps[] = [];

    if (view.detailedByDay) {
        leadingColumns.push(getDateColumn());
    } else if (view.isProjectSummaryView) {
        leadingColumns.push(getProjectGroupColumn(), getClientGroupColumn());
    }

    if (view.showClientColumn) {
        leadingColumns.push(getClientColumn(view));
    }

    if (view.detailedByDay) {
        if (view.isProjectDetailedView) {
            detailedColumns.push(getTypeColumn());
            if (!view.hideProjectDetailedCommentColumn) {
                detailedColumns.push(getProjectColumn(view));
            }
        } else {
            if (!view.hideProjectDetailedCommentColumn) {
                detailedColumns.push(getProjectColumn(view));
            }
            detailedColumns.push(getTypeColumn());
            if (view.isEmployeeDetailedView && view.showDetailedComment) {
                detailedColumns.push(getCommentColumn());
            }
        }
    } else if (!view.hideProjectDetailedCommentColumn && !view.isProjectSummaryView) {
        detailedColumns.push(getProjectColumn(view));
    }

    return assignCollapseButton([
        ...leadingColumns,
        ...detailedColumns,
        ...getHourColumns(view),
    ]);
};

const buildTemplateColumns = (columns: GridColumnProps[]): React.CSSProperties => {
    const values: string[] = [];

    columns.filter((column) => column.visible === undefined || column.visible)
        .forEach((column: GridColumnProps) => {
            const width = column.width ? `${column.width}px` : 'auto';
            const flexed = column.flex !== undefined
                ? typeof column.flex === 'boolean'
                    ? '1fr'
                    : `${column.flex.grow}fr`
                : null;

            values.push(flexed ?? width);
        });

    return { gridTemplateColumns: values.join(' ') };
};

interface CreateHeaderCellsParams {
    visibleColumns: GridColumnProps[];
    intl: IntlShape;
    collapseLabel: string;
    isAnyRowExpanded: boolean;
    onCollapseToggle: () => void;
    detailedByDay: boolean;
    groupType: TimeSheetGroupType;
    hideDetailedComment: boolean;
}

const createHeaderCells = ({
    visibleColumns,
    intl,
    collapseLabel,
    isAnyRowExpanded,
    onCollapseToggle,
    detailedByDay,
    groupType,
    hideDetailedComment,
}: CreateHeaderCellsParams): React.ReactNode[] => {
    const cells: React.ReactNode[] = [];
    const isProjectDetailedView = detailedByDay && groupType === TimeSheetGroupType.PROJECTVIEW;

    visibleColumns.forEach((column, index) => {
        if (column.noHeader) {
            return;
        }

        const columnLabel = column.localizationKey
            ? intl.formatMessage({ id: column.localizationKey })
            : column.field;
        const resolvedLabel = isProjectDetailedView && column.field === 'project' && !hideDetailedComment
            ? 'Comment'
            : column.field === 'comment'
                ? 'Comment'
                : column.field === 'projectGroup'
                    ? 'Project/Absence'
                    : columnLabel;

        if (column.isHeaderCollapseButton) {
            cells.push(
                <div
                    key={column.field ?? `column-${index}`}
                    className={classNames(styles.customHeaderCell, styles.collapseHeaderCell, {
                        [styles.collapseHeaderCellMerged]: detailedByDay
                    })}>
                    <CollapseButton
                        displayText={collapseLabel}
                        isExpanded={isAnyRowExpanded}
                        onExpandClick={onCollapseToggle}
                        containerClassName={styles.collapseButtonContainer}
                        displayTextClassName={styles.collapseButtonText}
                    />
                </div>
            );
            return;
        }

        const isNumeric = column.type === 'number';
        const headerClasses = classNames(
            column.field === 'project'
            || column.field === 'projectGroup'
            || column.field === 'clientGroup'
            || column.field === 'comment'
            || column.field === 'type'
                ? styles.customHeaderCell
                : classNames(
                    styles.customHeaderCell,
                    styles.hoursHeaderCell,
                    { [styles.numericHeaderCell]: isNumeric }
                )
        );
        const headerContent = column.headerTooltipKey
            ? (
                <Tooltip title={intl.formatMessage({id: column.headerTooltipKey})}>
                    <span className={styles.headerText}>{resolvedLabel}</span>
                </Tooltip>
            )
            : <span className={styles.headerText}>{resolvedLabel}</span>;

        cells.push(
            <div
                key={column.field ?? `column-${index}`}
                className={headerClasses}>
                {headerContent}
            </div>
        );
    });

    return cells;
};

const TimeReportGrid = (props: TimeReportGridProps, ref: React.Ref<ExpandedGroupsRef>) => {
    const { data, groupBy: groupType, detailedByDay } = props;
    const hideDetailedComment = useMediaQuery({ maxWidth: 1249 });
    const useWideSummaryColumns = useMediaQuery({ minWidth: 1101 });
    const [isAnyRowExpanded, setIsAnyRowExpanded] = useState(false);
    const gridRef = React.useRef<GridRef>(null);

    // Read on demand so the page need not mirror what is open.
    React.useImperativeHandle(ref, () => ({
        getExpandedGroupKeys: () => gridRef.current?.getExpandedRowKeys() ?? []
    }), []);
    const columns = useMemo(
        () => buildTimeReportColumns(getViewConfig(groupType, detailedByDay, hideDetailedComment, useWideSummaryColumns)),
        [detailedByDay, groupType, hideDetailedComment, useWideSummaryColumns]
    );
    const groupBy = useMemo(
        () => getGroupBy(groupType, detailedByDay, props.employeeTotals),
        [detailedByDay, groupType, props.employeeTotals]
    );
    const visibleColumns = useMemo(
        () => columns.filter((column) => column.visible === undefined || column.visible),
        [columns]
    );
    const headerTemplateColumns = useMemo(
        () => buildTemplateColumns(columns),
        [columns]
    );
    const gridInstanceKey = useMemo(
        () => `${groupType}-${detailedByDay}-${hideDetailedComment ? 'compact' : 'full'}-${useWideSummaryColumns ? 'wide' : 'narrow'}`,
        [detailedByDay, groupType, hideDetailedComment, useWideSummaryColumns]
    );

    const rows = React.useMemo(() => data, [data]);

    // No local reset on data/view changes: the grid reports its own state (including a single group
    // auto-expanded on mount), and resetting here left the button offering "expand" on an open group.

    const collapseLabel = isAnyRowExpanded
        ? props.intl.formatMessage({ id: Lx.General.COLLAPSE_ALL })
        : props.intl.formatMessage({ id: Lx.General.EXPAND_ALL });

    const headerCells = useMemo(() => createHeaderCells({
        visibleColumns,
        intl: props.intl,
        collapseLabel,
        isAnyRowExpanded,
        onCollapseToggle: () => gridRef.current?.collapseRows(),
        detailedByDay,
        groupType,
        hideDetailedComment,
    }), [collapseLabel, detailedByDay, groupType, hideDetailedComment, isAnyRowExpanded, props.intl, visibleColumns]);

    return (
        <div className={classNames(styles.gridWrapper, styles.grid)}>
            <div className={styles.customHeaderWrapper}>
                <div className={styles.customHeader} style={headerTemplateColumns}>
                    {headerCells}
                </div>
            </div>
            <TimiDataGrid
                key={gridInstanceKey}
                hideHeader
                columns={columns}
                rows={rows}
                groupBy={groupBy}
                intl={props.intl}
                className={styles.grid}
                disableBodyScroll
                autoExpandSingleGroup
                initialExpandedRowKeys={props.initialExpandedGroupKeys}
                ref={gridRef}
                onAnyRowExpand={setIsAnyRowExpanded} />
        </div>)
}

export default React.forwardRef(TimeReportGrid) as (
    props: TimeReportGridProps & React.RefAttributes<ExpandedGroupsRef>
) => React.JSX.Element;
